﻿import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PatientDetailsHeader from '../../../components/Account/PatientDetailsHeader';
import heatmapOverlay from '../../../assets/images/Mask_Lmdpi.png';
import heatmapBackground from '../../../assets/images/sensorInsoles.png';
import { AlertList } from '../../../components/documented/AlertList/AlertList';
import Heatmap from '../../../components/Heatmap/Heatmap';
import { HeatmapSizeEnum } from '../../../components/Heatmap/HeatmapSizeEnum';
import { temperatureTransforms } from '../../../components/Heatmap/heatmapTransforms';
import { useClientTheme } from '../../../helpers/hooks/useClientTheme';
import {
  applyOffsetToTimestampMs,
  getEnumKeyByEnumValue,
  isNullOrUndefined,
} from '../../../helpers/utilityFunctions/utilityFunctions';
import { AlertAnnotation } from '../../../model/passive/alertMoment';
import { HeatmapData } from '../../../model/passive/heatmapData';
import { PassiveParameterType } from '../../../model/passive/passiveParameterType';
import {
  clearAllAlertDetails,
  getAllAlertDetail,
} from '../../../redux/actions/passiveParameterActions';
import { setDailyMetricDate } from '../../../redux/actions/dailyMetricActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectedDailyMetrics } from '../../../redux/selectors/dailyMetricsSelectors';
import {
  dailyMetricIsLoading,
  selectedAlertDetails,
} from '../../../redux/selectors/passiveParametersSelectors';
import TemperatureInterpretationTableComponent from './TemperatureInterpretationTableComponent';
import TemperatureDetailContent from '../DailyParameters/TemperatureDetail/TemperatureDetailContent';
import AlertDetailHeader from './AlertDetailHeader';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexFlow: 'column',
    height: '100vh',
    margin: '12px 40px 0px 0px',
    backgroundColor: 'white',
    borderRadius: '40px 40px 0px 10px',
  },
  header: {
    margin: '40px 0px 0px 30px',
    backgroundColor: 'white',
  },
  content: {
    display: 'flex',
    flexFlow: 'column',
    backgroundColor: 'white',
  },
  backButton: {
    width: '40px',
    height: '40px',
  },
  backIcon: {
    transform: 'scale(1.3)',
  },
}));


/**
 * Dev-only Temperature page that includes both Temperature Distribution and Temperature Trends sections.
 * This is a copy of the production temperature page with the Temperature Trends feature enabled.
 */
const TemperatureDevPage: React.FC = () => {
  const classes = useStyles();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { t } = useTranslation('alertDetail');
  const { theme } = useClientTheme();

  const [selectedMomentId, setSelectedMomentId] = React.useState(0);

  const selectedData = useSelector(selectedDailyMetrics);
  const selectedAlertData = useSelector(selectedAlertDetails);
  const loading = useSelector(dailyMetricIsLoading);

  const alertType = PassiveParameterType.AlarmTemperature;

  const getAlertColor = (alertLevel: number): string => {
    switch (alertLevel) {
      case 1: return theme.palette.alertLevel1.main;
      case 2: return theme.palette.alertLevel2.main;
      default: return theme.palette.alertLevel3.main;
    }
  };

  const alertAnnotations = useMemo<AlertAnnotation[]>(() => {
    return selectedAlertData.flatMap((moment) =>
      moment.data.map((alert) => ({
        timestamp: applyOffsetToTimestampMs(alert.dateTime, alert.utcTimeZone),
        color: getAlertColor(alert.alertLevel),
        alertId: alert.id,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlertData, theme]);

  const handleAlertAnnotationClick = (alertId: number) => {
    setSelectedMomentId(alertId);
  };

  const createHeatmapData = () => {
    if (selectedMomentId === 0) {
      return new HeatmapData('[]', '[]');
    }

    const foundAlert = selectedAlertData
      .flatMap((moment) => moment.data)
      .find((alert) => alert.id === selectedMomentId);

    if (!isNullOrUndefined(foundAlert)) {
      return new HeatmapData(foundAlert.sensorDataLeft, foundAlert.sensorDataRight);
    }
    return new HeatmapData('[]', '[]');
  };

  useEffect(() => {
    const processedType =
      getEnumKeyByEnumValue(PassiveParameterType, alertType) || '';

    dispatch(
      getAllAlertDetail(
        patientId || '',
        processedType,
        theme.metrics.temperature.unit
      )
    );
    if (!isNullOrUndefined(selectedData?.latestAlertsTemperature)) {
      setSelectedMomentId(selectedData?.latestAlertsTemperature.id!);
    }

    return () => {
      dispatch(clearAllAlertDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: number
  ) => {
    setSelectedMomentId(id);

    const clickedAlert = selectedAlertData
      .flatMap((moment) => moment.data)
      .find((alert) => alert.id === id);

    if (clickedAlert) {
      dispatch(setDailyMetricDate(new Date(clickedAlert.dateTime)));
    }
  };

  return (
    <Box>
      <PatientDetailsHeader selectedPatientId={patientId} />

      <div className={classes.root}>
        <div className={classes.header}>
          <IconButton
            className={classes.backButton}
            color="primary"
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon className={classes.backIcon} />
          </IconButton>
        </div>
        <div className={classes.content}>
          <Box sx={{ margin: '0px 40px 40px 40px' }}>
            <AlertDetailHeader patientId={patientId} alertType="temperature" />

            {/* Temperature Distribution Section */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6, lg: 8 }}>
                <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body1">
                    {t('intro.temperature')}
                  </Typography>
                  <Heatmap
                    height={260}
                    width={260}
                    magnitude={HeatmapSizeEnum.Large}
                    data={createHeatmapData()}
                    type={alertType}
                    unit={theme.metrics.temperature.unit}
                    background={heatmapBackground}
                    overlay={heatmapOverlay}
                    transforms={temperatureTransforms}
                  />
                  <TemperatureInterpretationTableComponent />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                  <AlertList
                    alertType={alertType}
                    selectedAlertData={selectedAlertData}
                    selectedMomentId={selectedMomentId}
                    isLoading={loading}
                    onClick={handleListItemClick}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Temperature Trends Section - Dev only feature */}
            <Box sx={{ mt: 6 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h5"
                  component="h4"
                  sx={{ fontWeight: 'bold' }}
                >
                  {t('trends.title')}
                </Typography>
              </Box>
              <TemperatureDetailContent patientId={patientId} alertAnnotations={alertAnnotations} onAlertAnnotationClick={handleAlertAnnotationClick} />
            </Box>
          </Box>
        </div>
      </div>
    </Box>
  );
};

export default TemperatureDevPage;

